import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { Bell, BellOff, Mail, MessageSquare, ChevronDown, ChevronRight, ExternalLink, Save } from 'lucide-react';

interface Bindings { email?: string | null; sms?: string | null; push?: string | null }
interface Trigger { id: string; key: string; name?: string; description?: string; enabled: boolean; bindings: Bindings; variables?: string[]; is_system?: boolean }
interface Tpl { id: string; channel: string; name: string }
interface KnownCategory { key: string; label: string }
interface Prefs {
  muted_categories: string[];
  digest_categories: string[];
  known_categories: KnownCategory[];
}

/**
 * Notifications = the single "system event -> Email/SMS template" mapping,
 * built on the REAL communication triggers (what actually sends mail/SMS).
 * Template wording is authored on the Communications page. Super Admins only.
 */
export const NotificationsSettings: React.FC = () => {
  const { role } = useAuth();
  const navigate = useNavigate();
  const canEdit = role?.key === 'super_admin' || role?.key === 'master_admin';
  const [triggers, setTriggers] = useState<Trigger[] | null>(null);
  const [templates, setTemplates] = useState<Tpl[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    const [t, tp] = await Promise.all([
      api.get<Trigger[]>('/communications/triggers'),
      api.get<Tpl[]>('/communications/templates'),
    ]);
    setTriggers(t.data); setTemplates(tp.data);
  };
  useEffect(() => { void load(); }, []);
  if (!triggers) return <div>Loading…</div>;

  const emailTpls = templates.filter(x => x.channel === 'email');
  const smsTpls = templates.filter(x => x.channel === 'sms');

  const patchTrigger = async (tr: Trigger, patch: Partial<Trigger>): Promise<void> => {
    setSavingId(tr.id);
    const body: { enabled?: boolean; bindings?: Bindings } = {};
    if ('enabled' in patch) body.enabled = patch.enabled;
    if ('bindings' in patch) body.bindings = patch.bindings;
    try {
      await api.patch(`/communications/triggers/${tr.id}`, body);
      setTriggers(prev => (prev || []).map(x => x.id === tr.id ? { ...x, ...patch } : x));
      toast.success('Notification updated');
    } catch { toast.error('Update failed'); }
    finally { setSavingId(null); }
  };
  const setBinding = (tr: Trigger, channel: 'email' | 'sms', templateId: string): void => {
    void patchTrigger(tr, { bindings: { ...(tr.bindings || {}), [channel]: templateId || null } });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>
            Each system event sends the Email and/or SMS template you associate here — this is what actually gets delivered.
            Edit the wording of a template on the Communications page.{canEdit ? '' : ' Read-only — ask a Super Admin to change associations.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {triggers.map(tr => {
              const open = expanded === tr.id;
              return (
                <div key={tr.id} className="rounded-lg border border-border/60 overflow-hidden" data-testid={`notif-trigger-${tr.key}`}>
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-raptor-neutral/30">
                    <button type="button" className="flex items-center gap-2 text-left flex-1 min-w-0" onClick={() => setExpanded(open ? null : tr.id)} data-testid={`notif-expand-${tr.key}`}>
                      {open ? <ChevronDown size={15} className="shrink-0" /> : <ChevronRight size={15} className="shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-raptor-navy truncate">{tr.name || tr.key}</div>
                        <div className="text-[11px] text-raptor-navy/50 font-tabular truncate">{tr.key}</div>
                      </div>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant={tr.bindings?.email ? 'blue' : 'muted'} className="text-[10px]">Email {tr.bindings?.email ? '✓' : '—'}</Badge>
                      <Badge variant={tr.bindings?.sms ? 'cyan' : 'muted'} className="text-[10px]">SMS {tr.bindings?.sms ? '✓' : '—'}</Badge>
                      {tr.enabled ? <Badge variant="success" className="text-[10px]">On</Badge> : <Badge variant="muted" className="text-[10px]">Off</Badge>}
                      <Button size="sm" variant={tr.enabled ? 'ghost' : 'outline'} disabled={!canEdit || savingId === tr.id} onClick={() => void patchTrigger(tr, { enabled: !tr.enabled })} title={tr.enabled ? 'Disable' : 'Enable'} data-testid={`notif-toggle-${tr.key}`}>
                        {tr.enabled ? <Bell size={13} /> : <BellOff size={13} />}
                      </Button>
                    </div>
                  </div>
                  {open && (
                    <div className="p-3 space-y-3" data-testid={`notif-editor-${tr.key}`}>
                      {tr.description && <p className="text-xs text-raptor-navy/60 font-body">{tr.description}</p>}
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] font-semibold uppercase text-raptor-navy/70 flex items-center gap-1 mb-1"><Mail size={12} /> Email template</label>
                          <Select value={tr.bindings?.email || ''} disabled={!canEdit} onChange={e => setBinding(tr, 'email', e.target.value)} data-testid={`notif-email-tpl-${tr.key}`}>
                            <option value="">— none (no email) —</option>
                            {emailTpls.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold uppercase text-raptor-navy/70 flex items-center gap-1 mb-1"><MessageSquare size={12} /> SMS template</label>
                          <Select value={tr.bindings?.sms || ''} disabled={!canEdit} onChange={e => setBinding(tr, 'sms', e.target.value)} data-testid={`notif-sms-tpl-${tr.key}`}>
                            <option value="">— none (no SMS) —</option>
                            {smsTpls.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                        </div>
                      </div>
                      {tr.variables && tr.variables.length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold uppercase text-raptor-navy/50 mb-1">Available variables</div>
                          <div className="flex flex-wrap gap-1.5">
                            {tr.variables.map(v => <span key={v} className="rounded-md border border-raptor-blue/30 bg-raptor-blue/5 px-2 py-0.5 text-[11px] font-tabular text-raptor-blue">{`{{${v}}}`}</span>)}
                          </div>
                        </div>
                      )}
                      <button type="button" onClick={() => navigate('../communications')} className="text-xs font-semibold text-raptor-blue hover:underline inline-flex items-center gap-1" data-testid={`notif-edit-wording-${tr.key}`}>
                        <ExternalLink size={12} /> Edit template wording in Communications
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AlertPreferencesCard />
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════════
 * Per-user Alert Preferences — decides which alert categories ring the
 * bell in real-time versus which get muted into a quiet daily digest.
 *
 * Persists into ``notification_preferences`` (keyed by user_id) and is
 * read by the bell dropdown as a client-side filter.
 * ═══════════════════════════════════════════════════════════════════════ */
const AlertPreferencesCard: React.FC = () => {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<Prefs>('/notifications/preferences');
      setPrefs(r.data);
      setDirty(false);
    } catch (e) { console.debug('notifications prefs load skipped', e); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const setMode = useCallback((key: string, mode: 'ring' | 'digest' | 'mute') => {
    setPrefs(p => {
      if (!p) return p;
      const muted = new Set(p.muted_categories);
      const digest = new Set(p.digest_categories);
      muted.delete(key); digest.delete(key);
      if (mode === 'mute')   muted.add(key);
      if (mode === 'digest') digest.add(key);
      return { ...p, muted_categories: [...muted], digest_categories: [...digest] };
    });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      await api.put('/notifications/preferences', {
        muted_categories: prefs.muted_categories,
        digest_categories: prefs.digest_categories,
      });
      toast.success('Alert preferences saved');
      setDirty(false);
      // Nudge the bell to reload immediately.
      window.dispatchEvent(new Event('raptor:alert-prefs-changed'));
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }, [prefs]);

  if (!prefs) return null;

  const modeOf = (key: string): 'ring' | 'digest' | 'mute' => {
    if (prefs.muted_categories.includes(key)) return 'mute';
    if (prefs.digest_categories.includes(key)) return 'digest';
    return 'ring';
  };

  return (
    <Card data-testid="alert-prefs-card">
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="inline-flex items-center gap-2"><Bell size={16} /> Alert Preferences</CardTitle>
          <CardDescription>
            Choose which alert categories ring the bell immediately and which quietly bundle into
            a daily email digest. Applies to your account only.
          </CardDescription>
        </div>
        <Button onClick={() => void save()} disabled={!dirty || saving} data-testid="alert-prefs-save">
          <Save size={14} /> {saving ? 'Saving…' : 'Save preferences'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {prefs.known_categories.map(c => {
            const m = modeOf(c.key);
            return (
              <div key={c.key} className="flex items-center justify-between p-3 rounded-md border border-border/60 hover:bg-raptor-neutral/30 transition-colors"
                   data-testid={`alert-prefs-row-${c.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-raptor-navy">{c.label}</div>
                  <div className="text-[11px] text-raptor-navy/60 font-body">
                    {m === 'ring' && 'Rings the bell the moment it happens.'}
                    {m === 'digest' && 'Silenced in real-time — collected into a daily digest.'}
                    {m === 'mute' && 'Muted entirely. You will not see this category anywhere.'}
                  </div>
                </div>
                <div className="inline-flex rounded-md overflow-hidden border border-border/60">
                  <PrefButton active={m === 'ring'} onClick={() => setMode(c.key, 'ring')} testid={`alert-prefs-${c.key}-ring`}>
                    <Bell size={12} /> Ring
                  </PrefButton>
                  <PrefButton active={m === 'digest'} onClick={() => setMode(c.key, 'digest')} testid={`alert-prefs-${c.key}-digest`}>
                    <Mail size={12} /> Digest
                  </PrefButton>
                  <PrefButton active={m === 'mute'} onClick={() => setMode(c.key, 'mute')} testid={`alert-prefs-${c.key}-mute`}>
                    <BellOff size={12} /> Mute
                  </PrefButton>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-raptor-navy/50 font-body">
          Note: daily digest delivery is queued for a future release —
          for now the &quot;Digest&quot; setting silences the bell without pushing an email.
        </p>
      </CardContent>
    </Card>
  );
};

const PrefButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; testid?: string }> = ({ active, onClick, children, testid }) => (
  <button
    type="button" onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${
      active ? 'bg-raptor-blue text-white' : 'bg-white text-raptor-navy/70 hover:bg-raptor-neutral/50'
    }`}
  >{children}</button>
);

